import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  partnersApi,
  ACTIVITY_LABEL,
  tierChip,
  type PartnerActivity,
  type BoardRow,
} from "@/services/b2b";
import { PageShell, LoadingState, EmptyState } from "@/shared/ui/PageHeader";
import { formatCents } from "@/services/finance";
import { PE_QK, getPartnerEventQueue } from "@/services/partnerEvents";
import { Can } from "@/auth/permissions";
import { PartnerEventRows } from "./PartnerEventRows";

/**
 * 客户经理工作台 — 对齐 sign-off canvas「同业 B2B - 客户经理工作台」(铁律十九)。
 * 4 KPI + 🏆客户健康度仪表(按健康度倒序) + 📞本周待跟进 + 💳我的临时额度申请 + 📦我管客户最近订单。
 * 复用 _board（KPI/健康度行/最近订单）+ _credit-requests（额度申请）。
 */
const ACTIVITY_CHIP: Record<PartnerActivity, string> = {
  active: "bg-emerald-100 text-emerald-700",
  dormant: "bg-amber-100 text-amber-700",
  suspended: "bg-rose-100 text-rose-700",
};
const AVATAR_BG: Record<PartnerActivity, string> = { active: "bg-emerald-500", dormant: "bg-amber-500", suspended: "bg-rose-500" };

export function AccountManagerWorkbenchPage() {
  const boardQ = useQuery({ queryKey: ["b2b", "board"], queryFn: () => partnersApi.board() });
  const creditsQ = useQuery({ queryKey: ["b2b", "credit-requests", "pending"], queryFn: () => partnersApi.listAllCreditRequests("pending") });
  // PPR-3 F · 同行事件待处理队列（紧急在前、再按提交时间；口径同工作台 = 全部同行）
  const eventsQ = useQuery({ queryKey: PE_QK.queue, queryFn: () => getPartnerEventQueue("open"), refetchInterval: 60_000 });

  if (boardQ.isLoading) return <LoadingState />;
  if (boardQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>客户经理工作台加载失败</span>
      <button onClick={() => boardQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!boardQ.data) return <LoadingState />;
  const d = boardQ.data;
  const credits = creditsQ.data ?? [];
  const followUps = d.rows.filter((r) => r.activity !== "active");
  const thisMonth = new Date().toISOString().slice(0, 7);

  return (
    <PageShell
      title="💼 客户经理工作台"
      subtitle={`管 ${d.total} 家 B2B 客户 · 本周待跟进 ${followUps.length} 件 · 临时额度申请 ${credits.length} 件待老板批`}
      actions={
        <div className="flex gap-2">
          <Link to="/orders/checkout" className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> 代客提订单</Link>
          <Link to="/admin/channel-partners" className="flex h-9 items-center rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700">管理客户</Link>
        </div>
      }
    >
      {/* 4 KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="管理客户" value={String(d.total)} sub={d.tier_counts.map((t) => `${t.name} ${t.count}`).join(" + ") || "—"} />
        <Kpi label="本月新单" value={String(d.recent_orders.filter((o) => o.created_at.slice(0, 7) === thisMonth).length)} sub="B2B 渠道" tone="accent" />
        <Kpi label="待跟进" value={String(followUps.length)} sub="休眠 + 暂停" tone="warn" />
        <Kpi label="我的待审申请" value={String(credits.length)} sub="🔴 老板需处理" tone="danger" />
      </div>

      {/* PPR-3 F · 同行事件待处理：同行在同行站对订单提的需求，紧急在前；点订单号进订单详情回复 */}
      <Can perm="orders.orders.list">
        <section data-testid="partner-events-queue" className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3">
            <div className="text-[14px] font-semibold text-slate-900">📨 同行事件待处理（{eventsQ.data?.open_count ?? 0} 件）</div>
            <div className="text-[11px] text-slate-500">紧急的在前，再按提交时间（等得最久的在前）· 点订单号进订单详情回复</div>
          </div>
          {eventsQ.isError ? (
            <button type="button" onClick={() => eventsQ.refetch()} className="text-[12.5px] text-rose-600 underline">同行事件加载失败，点击重试</button>
          ) : eventsQ.isLoading ? <p className="text-[12.5px] text-slate-400">加载中…</p>
            : <PartnerEventRows rows={(eventsQ.data?.items ?? []).slice(0, 20)} empty="没有待处理的同行事件" showPartner />}
        </section>
      </Can>

      {/* 🏆 客户健康度仪表 */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3">
          <div className="text-[14px] font-semibold text-slate-900">🏆 我的客户健康度仪表</div>
          <div className="text-[11px] text-slate-500">按健康度倒序 · 红色客户优先跟进</div>
        </div>
        <div className="flex flex-col gap-2">
          {d.rows.map((r) => <HealthRow key={r.id} r={r} />)}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 📞 本周待跟进 */}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 text-[14px] font-semibold text-slate-900">📞 本周待跟进（{followUps.length} 件）</div>
          <div className="flex flex-col gap-2">
            {followUps.length === 0 && <div className="py-3 text-[12px] text-slate-400">客户都活跃 · 无待跟进</div>}
            {followUps.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium text-slate-800">{r.name}</div>
                  <div className="text-[11px] text-slate-500">{r.activity === "suspended" ? "已暂停 · 需谈合作" : `休眠 ${r.last_order_days ?? "—"} 天 · 需主动推产品`}</div>
                </div>
                <Link to={`/admin/channel-partners/${r.id}`} className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-[12px] font-medium text-white">跟进</Link>
              </div>
            ))}
          </div>
        </section>

        {/* 💳 我提交的临时额度申请 */}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 text-[14px] font-semibold text-slate-900">💳 我提交的临时额度申请（{credits.length} 件待老板批）</div>
          <div className="flex flex-col gap-2">
            {credits.length === 0 && <div className="py-3 text-[12px] text-slate-400">无待审申请</div>}
            {credits.map((c) => (
              <div key={c.id} className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium text-slate-800">+{formatCents(c.additional_credit_cents)}</span>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">待老板批</span>
                </div>
                <div className="mt-0.5 truncate text-[11px] text-slate-500">{c.reason}</div>
              </div>
            ))}
            <div className="mt-1 text-[10px] text-slate-400">💡 老板审通过后会通知 + 自动生效</div>
          </div>
        </section>
      </div>

      {/* 📦 我管的客户最近订单 */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold text-slate-900">📦 我管的客户最近订单（{d.recent_orders.length} 条 · 按时间倒序）</div>
        {d.recent_orders.length === 0 ? (
          <EmptyState message="暂无 B2B 客户订单" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-3 py-2 text-left">订单号</th><th className="px-3 py-2 text-left">客户</th><th className="px-3 py-2 text-right">人数</th><th className="px-3 py-2 text-right">金额</th><th className="px-3 py-2 text-left">状态</th></tr>
              </thead>
              <tbody>
                {d.recent_orders.slice(0, 30).map((o) => (
                  <tr key={o.order_no} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{o.order_no}</td>
                    <td className="px-3 py-2 text-slate-600">{o.customer}</td>
                    <td className="px-3 py-2 text-right">{o.pax}</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatCents(o.amount_cents)}</td>
                    <td className="px-3 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageShell>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "danger" | "accent" | "warn" }) {
  const color = tone === "danger" ? "#EF4444" : tone === "accent" ? "rgb(var(--c-blue-600))" : tone === "warn" ? "#F59E0B" : "#0F172A";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-[22px] font-bold leading-tight" style={{ color }}>{value}</div>
      <div className="mt-0.5 text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}

function HealthRow({ r }: { r: BoardRow }) {
  const last = r.last_order_days == null ? "无订单" : r.last_order_days === 0 ? "今日" : r.last_order_days === 1 ? "昨日" : `${r.last_order_days} 天前`;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[14px] font-bold text-white ${AVATAR_BG[r.activity]}`}>{r.name.slice(0, 1)}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-slate-900">{r.name}</span>
          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tierChip(r.tier)}`}>{r.tier_name}</span>
        </div>
        <div className="text-[11px] text-slate-500">上次：{last}</div>
      </div>
      <div className="hidden text-center sm:block"><div className="text-[13px] font-bold text-slate-800">{r.order_count}</div><div className="text-[10px] text-slate-400">单</div></div>
      <div className="hidden w-28 text-right md:block"><div className="text-[12px] font-medium text-slate-700">{formatCents(r.used_cents)} / {formatCents(r.limit_cents)}</div><div className="text-[10px] text-slate-400">月均 / 额度</div></div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ACTIVITY_CHIP[r.activity]}`}>{ACTIVITY_LABEL[r.activity]}</span>
      <Link to={`/admin/channel-partners/${r.id}`} className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">查看</Link>
    </div>
  );
}
